import type { Dispatch, FC } from 'react';
import { createContext, useContext, useMemo, useReducer } from 'react';

import type { TReducerAction } from './actions';
import { reducer } from './reducer';
import type { TState } from './state';
import { state as initialState } from './state';

export const FilterContext = createContext<{
    filterState: TState;
    filterDispatch: Dispatch<TReducerAction>;
}>({
    filterState: initialState,
    filterDispatch: () => null,
});

export const useFilterContext = () => {
    const contextValue = useContext(FilterContext);

    if (!contextValue)
        throw new Error(
            'useFilterContext must be used within FilterContextProvider scope'
        );

    return contextValue;
};

export const FilterContextProvider: FC = ({ children }) => {
    const [filterState, filterDispatch] = useReducer(reducer, initialState);

    return (
        <FilterContext.Provider
            value={useMemo(
                () => ({
                    filterState,
                    filterDispatch,
                }),
                [filterState, filterDispatch]
            )}
        >
            {children}
        </FilterContext.Provider>
    );
};

export * from './state';

export * from './reducer';

export * from './actions';
